import { z } from 'zod';
import { ActiveStatusSchema, MemberRoleSchema, RoleSchema, type Role } from './enums';

// ---------------------------------------------------------------- senha
/** Política de senha: mínimo 8 caracteres, com letras e números. */
export const NewPasswordSchema = z
  .string()
  .min(8, 'A senha deve ter ao menos 8 caracteres')
  .max(128, 'Senha muito longa')
  .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), 'Use letras e números');

/** Troca de senha (primeiro acesso ou voluntária). A senha fica só no Firebase Authentication. */
export const ChangePasswordSchema = z
  .object({ newPassword: NewPasswordSchema, confirmPassword: z.string() })
  .refine((v) => v.newPassword === v.confirmPassword, { path: ['confirmPassword'], message: 'As senhas não conferem' });
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;

// ---------------------------------------------------------------- usuários da escola
/** Hierarquia para conceder/gerenciar acessos (não se concede papel acima do próprio). */
export const ROLE_RANK: Record<Role, number> = {
  PLATFORM_ADMIN: 100,
  NETWORK_ADMIN: 50,
  SCHOOL_DIRECTOR: 40,
  COORDINATOR: 30,
  TEACHER: 20,
  VIEWER: 10,
};

export const CreateSchoolUserSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email('Informe um e-mail válido')),
  displayName: z.string().trim().min(3, 'Informe o nome').max(120),
  role: MemberRoleSchema,
  teacherId: z.string().trim().min(1).max(128).nullish(),
  /** Senha provisória definida pelo administrador; vazia → gerada automaticamente. */
  temporaryPassword: z
    .string()
    .trim()
    .transform((v) => v || undefined)
    .pipe(NewPasswordSchema.optional())
    .optional(),
});
export type CreateSchoolUserInput = z.input<typeof CreateSchoolUserSchema>;
export type CreateSchoolUserData = z.output<typeof CreateSchoolUserSchema>;

export const UpdateSchoolUserSchema = z
  .object({ role: MemberRoleSchema.optional(), status: ActiveStatusSchema.optional(), teacherId: z.string().trim().min(1).max(128).nullish() })
  .refine((v) => v.role !== undefined || v.status !== undefined || v.teacherId !== undefined, 'Nada para atualizar');
export type UpdateSchoolUserInput = z.infer<typeof UpdateSchoolUserSchema>;

export const ResetPasswordSchema = z.object({
  temporaryPassword: z
    .string()
    .trim()
    .transform((v) => v || undefined)
    .pipe(NewPasswordSchema.optional())
    .optional(),
});
export type ResetPasswordInput = z.input<typeof ResetPasswordSchema>;

export const SchoolUserSchema = z.object({
  /** Chave composta: e-mail + código da escola (ex.: diretoria@escola.gov.br_3512-3456). */
  key: z.string(),
  uid: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: RoleSchema,
  status: ActiveStatusSchema,
  teacherId: z.string().nullable(),
  /** Ainda não trocou a senha provisória. */
  mustChangePassword: z.boolean(),
  lastLoginAt: z.string().nullable(),
  passwordChangedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type SchoolUser = z.infer<typeof SchoolUserSchema>;

export const CreateSchoolUserResponseSchema = z.object({
  user: SchoolUserSchema,
  /** Senha provisória para entregar ao usuário (exibida uma única vez; nunca armazenada). */
  temporaryPassword: z.string().nullable(),
  /** A pessoa já tinha conta (outra escola): continua com a senha atual. */
  existingAccount: z.boolean(),
});
export type CreateSchoolUserResponse = z.infer<typeof CreateSchoolUserResponseSchema>;

export const ResetPasswordResponseSchema = z.object({ temporaryPassword: z.string() });
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;
